/**
 * Pure grid pathfinding helpers. Used by GameScene when an organ moves so its
 * connected pipes can re-route to follow the moved port without forcing the
 * student to redraw them.
 *
 * No Phaser dependency — testable in isolation.
 */

import type { Cell, Direction, SegmentSpec } from '../types';
import { elbowFor, straightFor } from '../types';

/**
 * Horizontal-first Manhattan L-path between two cells. Returns one SegmentSpec
 * per cell along the route, with the corner cell flagged as an elbow shape
 * matching the entering+leaving directions.
 *
 * @param from         start cell (becomes segments[0])
 * @param to           end cell (becomes segments[last])
 * @param startFacing  direction the path "leaves" the start cell — used for
 *                     the first segment's shape (straight-h or straight-v).
 *                     Caller usually passes the source port's facing.
 */
export function manhattanPath(from: Cell, to: Cell, startFacing: Direction): SegmentSpec[] {
  const segments: SegmentSpec[] = [];
  let col = from.col;
  let row = from.row;
  segments.push({ col, row, shape: straightFor(startFacing), enteringDir: startFacing });

  const colStep = Math.sign(to.col - col);
  while (col !== to.col) {
    col += colStep;
    const dir: Direction = colStep > 0 ? 'E' : 'W';
    segments.push({ col, row, shape: straightFor(dir), enteringDir: dir });
  }
  const rowStep = Math.sign(to.row - row);
  while (row !== to.row) {
    row += rowStep;
    const dir: Direction = rowStep > 0 ? 'S' : 'N';
    segments.push({ col, row, shape: straightFor(dir), enteringDir: dir });
  }

  // Mark direction changes as elbows on the cell where the turn happens.
  for (let i = 1; i < segments.length; i++) {
    if (segments[i].enteringDir !== segments[i - 1].enteringDir) {
      const elbow = elbowFor(segments[i - 1].enteringDir, segments[i].enteringDir);
      if (elbow) segments[i - 1].shape = elbow;
    }
  }
  return segments;
}
